import {
  Directive,
  forwardRef,
  input,
  InputSignal,
  effect
} from '@angular/core';
import {
  NG_VALIDATORS
} from '@angular/forms';
import type {
  Provider,
  Type
} from '@angular/core';
import type {
  AbstractControl,
  ValidationErrors,
  Validator
} from '@angular/forms';
import type { SelectedFile } from './file-upload.component';

export const FILE_UPLOAD_DUPLICATE_VALIDATOR: Provider = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef((): Type<FileUploadDuplicateValidator> => FileUploadDuplicateValidator),
  multi: true
};

/**
 * FileUploadDuplicateValidator Directive
 *
 * Validates the size of all selected files added in a file input in bytes
 *
 * @example
 * <td-wb-file-upload tdWbFileUploadDuplicateValidator [(ngModel)]="files"></td-wb-file-upload>
 */
@Directive({
  selector: '[tdWbFileUploadDuplicateValidator][ngModel]',
  providers: [FILE_UPLOAD_DUPLICATE_VALIDATOR]
})
export class FileUploadDuplicateValidator implements Validator {
  // Typed as possibly-undefined so the `!== undefined` guard below is type-safe.
  private onChange?: () => void;
  readonly enabled: InputSignal<boolean> = input<boolean>(false, { alias: 'tdWbFileUploadDuplicateValidator' });
  private _prevEnabled: boolean | undefined;

  constructor() {
    // effect: calls Angular Forms' registerOnValidatorChange() when constraint inputs change —
    // this directive uses effect() as a reactive alternative to implementing OnChanges.
    effect(() => {
      const v = this.enabled();
      if (v && this._prevEnabled !== v && this.onChange !== undefined) {
        this.onChange();
      }
      this._prevEnabled = v;
    });
  }

  validate(fileUploadControl: AbstractControl): ValidationErrors | null {
    if (fileUploadControl.value !== undefined && fileUploadControl.value !== null) {
      return this.findDuplicates(fileUploadControl.value);
    }
    return null;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onChange = fn;
  }

  private findDuplicates(files: Array<SelectedFile>): ValidationErrors | null {
    for (const file of files) {
      const duplicates = files.filter((f: SelectedFile) => f.fileName === file.fileName);
      if (duplicates.length > 1) {
        return { DUPLICATES: true };
      }
    }
    return null;
  }
}
